<template>
  <div class="toast-container">
    <transition-group name="toast" tag="div">
      <div v-for="toast in toasts" :key="toast.id" class="toast" :class="toast.type">
        {{ toast.message }}
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

interface ToastEntry {
  id: number
  message: string
  type: 'success' | 'error'
}

const toasts = ref<ToastEntry[]>([])
let counter = 0

function addToast(entry: Omit<ToastEntry, 'id'>) {
  const id = counter++
  toasts.value.push({ ...entry, id })
  setTimeout(() => removeToast(id), 4000)
}

function removeToast(id: number) {
  toasts.value = toasts.value.filter((toast) => toast.id !== id)
}

function listener(event: Event) {
  const detail = (event as CustomEvent<{ type?: 'success' | 'error'; message: string }>).detail
  if (!detail?.message) return
  addToast({ message: detail.message, type: detail.type ?? 'success' })
}

onMounted(() => {
  window.addEventListener('toast', listener as EventListener)
})

onBeforeUnmount(() => {
  window.removeEventListener('toast', listener as EventListener)
})
</script>

<style scoped>
.toast-container {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  display: grid;
  gap: 0.75rem;
  z-index: 9999;
}

.toast {
  min-width: 220px;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.85rem;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.25);
  color: #fff;
  background: rgba(15, 23, 42, 0.92);
  border: 1px solid rgba(96, 165, 250, 0.35);
}

.toast.success {
  border-color: rgba(34, 197, 94, 0.4);
  background: rgba(21, 128, 61, 0.92);
}

.toast.error {
  border-color: rgba(248, 113, 113, 0.4);
  background: rgba(153, 27, 27, 0.92);
}

.toast-enter-active,
.toast-leave-active {
  transition: all 240ms ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
